/**
 * Registering a patient who is not in the record (DECISIONS.md #40).
 *
 * **A reservnummer read off a pre-printed band, never a number this screen invents.** Phase 0 does
 * not generate identifiers — `PHASE-0.md` §4 lists generation as out of scope, and a box that
 * issued numbers during an outage would issue the same block twice as soon as a second box did.
 * The form cannot represent a personnummer either: three letters and eight digits is a shape a
 * real one cannot take, and {@link registrationProblem} refuses one **before it is sent**, so a
 * mistyped personnummer never reaches a request body or a log (AGENTS.md §6).
 *
 * **The ward is chosen here, and it is the same choice the ward list is making** (DECISIONS.md
 * #47, amending #40). The dropdown is bound to the selection `App` holds, so picking a ward here
 * moves the list behind this form too — one piece of state, two controls onto it. #40 kept the
 * ward out of this screen to stop two controls disagreeing, and that risk is real; what it costs
 * is that a clinician who notices the wrong ward *while holding a wristband* has to cancel, change
 * it on the list, and type everything again, because nothing is saved until Register (#38). A
 * second source of truth would be the defect — a second control onto one is not.
 *
 * **There is no "no ward" option.** Registration writes three rows in one transaction precisely so
 * that a patient cannot exist off every ward list, and it is the only path in the API that writes
 * an encounter: `PatientCorrection` refuses to touch ward or bed, so a patient registered without
 * one could never be placed afterwards. For the patient who has arrived but has no bed yet, the
 * ward is theirs and the **bed is left empty** — `WardListQuery` sorts those last and shows the gap
 * rather than hiding them, which is the case the bed field's hint now names.
 *
 * **Nothing is saved until Register**, as on the note editor and for the same reason (#38): there
 * is no draft table and no `localStorage`, so the form warns before discarding and before the tab
 * closes.
 *
 * **Register is one click and it is final.** No confirmation step — a clinician who has typed a
 * wristband number and pressed it meant to, and an extra dialogue is the thing that gets clicked
 * through. What the screen does instead is say plainly, before the button, that a registered
 * patient cannot be removed (AGENTS.md §3 rule 2).
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isInternetDemo } from '../DeploymentNotice'
import { mayNotDocumentMessage } from '../auth/authDisplay'
import type { Session } from '../auth/authApi'
import { useWardDirectory } from '../ward/wardDirectory'
import {
  registerPatient,
  RegistrationRefusedError,
  RegistrationUnavailableError,
  type RegisteredPatient,
  type RegistrationRefusalCode,
  type RegistrationRefusalParams,
} from './registrationApi'
import {
  draftProblemMessage,
  offeredSexLabel,
  registrationFieldHint,
  registrationFieldLabel,
  registrationRefusalMessage,
  registrationUnavailableMessage,
  registrationWardLabel,
  sexNotAnsweredLabel,
  type RegistrationTranslate,
} from './registrationDisplay'
import {
  emptyDraft,
  isDraftDirty,
  IDENTIFIER_INPUT_MAX_LENGTH,
  MAXIMUM_FIELD_LENGTH,
  OFFERED_SEXES,
  registrationProblem,
  toSubmission,
  type RegistrationDraft,
  type RegistrationField,
} from './registrationDraft'

type Phase =
  | { readonly kind: 'editing' }
  /** A write is in flight. The form is read-only and Register cannot be pressed twice. */
  | { readonly kind: 'registering' }
  /** Written. The form is gone, so the same number cannot be registered a second time. */
  | { readonly kind: 'registered'; readonly patient: RegisteredPatient }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: RegistrationRefusalCode | 'UNKNOWN'
      readonly params: RegistrationRefusalParams
    }
  | { readonly kind: 'unavailable' }
  /** Something this side found before sending — same wording as the server's. */
  | { readonly kind: 'problem'; readonly message: string }

/** Which fields are a courtesy to leave empty, for the `(frivilligt)` marker. */
const OPTIONAL_FIELDS: Readonly<Record<RegistrationField, boolean>> = {
  identifier: false,
  assigner: false,
  givenName: true,
  // Required unless the identity is marked as assumed, which the hint says. Not marked optional,
  // because for a patient who can be identified it is not.
  familyName: false,
  birthDate: true,
  bed: true,
}

function TextField({
  field,
  value,
  disabled,
  autoFocus,
  onChange,
  t,
}: {
  readonly field: RegistrationField
  readonly value: string
  readonly disabled: boolean
  readonly autoFocus: boolean
  readonly onChange: (value: string) => void
  readonly t: RegistrationTranslate
}) {
  const id = `registration-field-${field}`
  const hintId = `${id}-hint`

  // The reservnummer is the only field with a fixed width, because it is the only one whose length
  // is known (`maxLength` above) and the only one read back character by character against a band.
  // A modifier rather than an id selector in the stylesheet, which uses none.
  const modifier = field === 'identifier' ? ' registration__field--identifier' : ''

  return (
    <div className={`registration__field${modifier}`}>
      <label htmlFor={id}>
        {registrationFieldLabel(field, t)}
        {OPTIONAL_FIELDS[field] && (
          <span className="registration__optional"> ({t('registration:screen.optional')})</span>
        )}
      </label>

      <p id={hintId} className="registration__hint">
        {registrationFieldHint(field, t)}
      </p>

      {/* On the internet demo host only (DECISIONS.md #58): testers have no wristband to read, and the
          box generates no numbers (#40), so they are told which made-up range to use. The real hint
          above stays, because reading the number off a band is the workflow they are judging. */}
      {field === 'identifier' && isInternetDemo(import.meta.env.VITE_CONTINUUM_DEPLOYMENT) && (
        <p className="registration__hint registration__hint--demo">
          {t('registration:fieldHint.identifierDemo')}
        </p>
      )}

      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-describedby={hintId}
        // **`type="text"` for the birth date too, deliberately.** A native date input renders and
        // accepts the browser's locale format, so the same box would read `03/04/1950` on one
        // workstation and `1950-04-03` on the next — and which of April and March that is depends
        // on a setting nobody checked. The record has one date format (AGENTS.md §5), the hint
        // names it, and what is typed is what is stored.
        //
        // `maxLength` as well as the check in `registrationProblem`, because stopping at the limit
        // is kinder than refusing a registration after 10,000 characters. The server still
        // enforces it — this is not the guard, it is the courtesy.
        maxLength={field === 'identifier' ? IDENTIFIER_INPUT_MAX_LENGTH : MAXIMUM_FIELD_LENGTH}
        // Upper case as typed, so what is on screen is what will be checked and sent. Cosmetic
        // only — `normaliseIdentifier` does it again on the value that leaves.
        style={field === 'identifier' ? { textTransform: 'uppercase' } : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

/**
 * Which ward the patient is being admitted to.
 *
 * A dropdown when there is a choice, and a stated fact when there is not — which is the opposite
 * call from `WardPicker`, where one ward renders nothing. There, the heading already names the ward
 * and the control is only about what to look at. Here the ward is about to become a row that no
 * `DELETE` can remove, so it is on screen either way: a clinician must be able to check it before
 * pressing Register, not infer it from a dropdown that is not there.
 *
 * It asks for the directory itself, which costs one audit row per form open. That is the right
 * trade — a read is supposed to be audited (AGENTS.md §4), and the alternative is `App` holding the
 * directory so that a screen that never opens still pays for it.
 *
 * @param ward The selected ward code, or null if the directory has not answered or could not be
 *   reached. `useWardDirectory` fills a null in once the answer arrives.
 * @param disabled While a write is in flight — the ward is part of what is being written.
 * @param onSelect Lifts the choice to `App`, which is where the ward list reads it from. Not local
 *   state: a second copy here is the disagreement #40 was protecting against.
 * @param t The screen's lookup.
 */
function WardField({
  ward,
  disabled,
  onSelect,
  t,
}: {
  readonly ward: string | null
  readonly disabled: boolean
  readonly onSelect: (ward: string) => void
  readonly t: RegistrationTranslate
}) {
  const wards = useWardDirectory(ward, onSelect)

  // No choice to offer: the box has not answered, could not be reached, or holds one ward. Say
  // which ward it is, or that there is none — and the second of those is what `registrationProblem`
  // refuses to submit on, so the sentence and the refusal agree.
  if (wards === null || wards.length <= 1) {
    return (
      <p className="registration__ward">
        {ward === null
          ? t('registration:screen.noWard')
          : t('registration:screen.admitsTo', { ward })}
      </p>
    )
  }

  const id = 'registration-field-ward'
  const hintId = `${id}-hint`

  return (
    <div className="registration__field">
      {/* `patient:overview.ward`, not a key of this screen's own: the box a clinician picks a ward
          in must be labelled with the word the value is read back under on the overview. Two
          catalogue entries holding `Avdelning` is a label that drifts the first time a reviewer
          rewords one (see `registrationDisplay.ts`). */}
      <label htmlFor={id}>{registrationWardLabel(t)}</label>

      <p id={hintId} className="registration__hint">
        {t('registration:fieldHint.ward')}
      </p>

      <select
        id={id}
        value={ward ?? ''}
        disabled={disabled}
        aria-describedby={hintId}
        onChange={(event) => onSelect(event.target.value)}
      >
        {/* No empty option, unlike the sex field: a patient is admitted somewhere, and an unchosen
            ward is not a state the record can hold. The directory has answered by the time this
            renders and the first ward is already selected. */}
        {wards.map((option) => (
          // The code and the name both, because staff say the code and the printed sheet carries
          // it. Assembled here rather than through the catalogue: `name` is record content and must
          // not pass through a translated message (AGENTS.md §7).
          <option key={option.ward} value={option.ward}>
            {option.ward} — {option.name}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * The registration form.
 *
 * @param ward The ward the patient is admitted to — the selection `App` holds, shared with the ward
 *   list, or null before the directory has answered. Null is a state the form reports rather than
 *   guesses past.
 * @param onWardChange Lifts a choice made in the ward dropdown back to `App` (#47).
 * @param session The signed-in clinician, for the courtesy check on `canDocument`.
 * @param onOpenPatient Opens the new patient's overview, from the confirmation. The form stays on
 *   that confirmation until the clinician leaves it — seeing the number in the record is part of
 *   filing it.
 * @param onClose Leaves the form.
 */
export function PatientRegistrationScreen({
  ward,
  onWardChange,
  session,
  onOpenPatient,
  onClose,
}: {
  readonly ward: string | null
  readonly onWardChange: (ward: string) => void
  readonly session: Session
  readonly onOpenPatient: (patientId: string) => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'auth', 'patient', 'registration'])

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [draft, setDraft] = useState<RegistrationDraft>(() => emptyDraft())
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /**
   * Whether a `setState` would still be seen.
   *
   * A submit in flight is deliberately **not** aborted on unmount — see `registerPatient`. A
   * write that reached the server happened, and cancelling the request would not undo it, it
   * would only hide the answer. So the request runs to completion and this stops it writing into
   * a component that is gone.
   */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const dirty = isDraftDirty(draft)
  const busy = phase.kind === 'registering'

  /**
   * The browser's own "leave this page?" question, while there is something typed.
   *
   * The only guard against a reload or a closed tab, and it is here because #38 means there is
   * nothing to come back to. The wording is the browser's and cannot be set, which is why the
   * on-screen sentence says it too.
   */
  useEffect(() => {
    if (!dirty || phase.kind === 'registered') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // Both, for the reason `NoteEditor` gives: `preventDefault()` alone is the current spec and
      // only Chrome 119 and later honour it, while Firefox and Safari cancel on a non-empty
      // `returnValue`. The value is never displayed, so it is not a catalogue key.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const update = <Field extends keyof RegistrationDraft>(
    field: Field,
    value: RegistrationDraft[Field],
  ) => {
    setDraft((previous) => ({ ...previous, [field]: value }))

    // A refusal describes a form that has since changed. Keeping it on screen while the clinician
    // fixes it would leave them reading an error about something they have already corrected.
    setNotice(null)
  }

  const submit = () => {
    if (busy) {
      return
    }

    // `new Date()` at the moment of the check rather than at render, so a form left open across
    // midnight does not refuse a birth date of today.
    const problem = registrationProblem(draft, ward, new Date())

    if (problem !== null) {
      // Checked here rather than by disabling the button. A greyed-out Register with no
      // explanation is the worse failure in a stressed setting: the clinician cannot tell whether
      // the system is broken or they have missed a field, and the message is the same one the
      // server would have sent.
      setNotice({ kind: 'problem', message: draftProblemMessage(problem, t) })
      return
    }

    if (ward === null) {
      // Unreachable — `registrationProblem` refuses a null ward above — and kept because it is
      // what narrows the type for `toSubmission`. A fallback ward code here would admit a patient
      // to a ward nobody chose, and there is no `DELETE` (AGENTS.md §3).
      setNotice({ kind: 'problem', message: t('registration:screen.noWard') })
      return
    }

    setPhase({ kind: 'registering' })
    setNotice(null)

    const controller = new AbortController()

    registerPatient(toSubmission(draft, ward), controller.signal).then(
      (patient) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'registered', patient })
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        // Back to editing with every field untouched, whatever went wrong. The form is the only
        // copy of what was read off the band.
        setPhase({ kind: 'editing' })

        if (cause instanceof RegistrationRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })
        } else if (cause instanceof RegistrationUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const heading = t('registration:screen.heading')

  // Withheld rather than disabled, for the reason `PatientOverview` gives: a form a role may not
  // use is better absent with a reason than present and refusing. A courtesy only — the gate is
  // `AccessGate` on the server, which refuses the write and audits the refusal (DECISIONS.md #39).
  if (!session.canDocument) {
    return (
      <section className="registration" aria-labelledby="registration-heading">
        <h2 id="registration-heading">{heading}</h2>

        <p className="registration__notice" role="status">
          {mayNotDocumentMessage(session, t)}
        </p>

        <button type="button" onClick={onClose}>
          {t('registration:screen.close')}
        </button>
      </section>
    )
  }

  if (phase.kind === 'registered') {
    return (
      <section
        className="registration registration--registered"
        aria-labelledby="registration-heading"
      >
        <h2 id="registration-heading">{heading}</h2>

        {/* `status`, not `alert`: it is good news, and it must not interrupt. */}
        <p className="registration__registered" role="status">
          {t('registration:screen.registeredOk', {
            // The identifier as it was sent, which is the string the clinician can compare
            // against the band in their hand.
            identifier: toSubmission(draft, phase.patient.ward).identifier,
          })}{' '}
          {t('registration:screen.registeredOnWard', { ward: phase.patient.ward })}
        </p>

        {/* The overview first: a patient who has just been registered is a patient somebody is
            about to document on. */}
        <button type="button" onClick={() => onOpenPatient(phase.patient.patientId)}>
          {t('registration:screen.openPatient')}
        </button>

        {/* A fresh form rather than a remount from above, so the ward and the session are
            untouched. The previous patient's details must not survive into it. */}
        <button
          type="button"
          onClick={() => {
            setDraft(emptyDraft())
            setNotice(null)
            setPhase({ kind: 'editing' })
          }}
        >
          {t('registration:screen.registerAnother')}
        </button>

        <button type="button" onClick={onClose}>
          {t('registration:screen.close')}
        </button>
      </section>
    )
  }

  return (
    <section className="registration" aria-labelledby="registration-heading">
      <header className="registration__header">
        <h2 id="registration-heading">{heading}</h2>

        {/* Said before the form: a clinician has to know the number comes off the band, and that
            a patient already in the record is found by searching, before they start typing. */}
        <p className="registration__explains">{t('registration:screen.explanation')}</p>
        <p className="registration__search-first">{t('registration:screen.searchFirst')}</p>
        {/* Said up front because it is the first thing a new user tries, and the refusal only
            arrives after they have typed one. A patient with a personnummer is in the imported
            record, and phase 0 holds no real personnummer at all (DECISIONS.md #40, AGENTS.md
            §6) — the form stops one at the keyboard, and this says so before the keyboard. */}
        <p className="registration__search-first">{t('registration:screen.noNationalNumber')}</p>
      </header>

      {/* First, and before the number: where the patient is going is the one thing on this form a
          clinician can get wrong without mistyping anything. Not in the header any more — a control
          belongs among the fields it is one of, and the header is the part that is read once. */}
      <WardField ward={ward} disabled={busy} onSelect={onWardChange} t={t} />

      <TextField
        field="identifier"
        value={draft.identifier}
        disabled={busy}
        // The cursor starts on the number, which is what the clinician is holding.
        autoFocus
        onChange={(value) => update('identifier', value)}
        t={t}
      />

      <TextField
        field="assigner"
        value={draft.assigner}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('assigner', value)}
        t={t}
      />

      <TextField
        field="givenName"
        value={draft.givenName}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('givenName', value)}
        t={t}
      />

      <TextField
        field="familyName"
        value={draft.familyName}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('familyName', value)}
        t={t}
      />

      <TextField
        field="birthDate"
        value={draft.birthDate}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('birthDate', value)}
        t={t}
      />

      <div className="registration__field">
        <label htmlFor="registration-field-sex">
          {t('registration:field.sex')}
          <span className="registration__optional"> ({t('registration:screen.optional')})</span>
        </label>

        <select
          id="registration-field-sex"
          value={draft.sex}
          disabled={busy}
          onChange={(event) => update('sex', event.target.value)}
        >
          {/* The empty option first, and it is not the same as `unknown`: nobody has answered,
              versus somebody established that it is not known. The record keeps them apart
              (`sex` is nullable with a CHECK on the values), so the form must too. */}
          <option value="">{sexNotAnsweredLabel(t)}</option>

          {OFFERED_SEXES.map((sex) => (
            <option key={sex} value={sex}>
              {offeredSexLabel(sex, t)}
            </option>
          ))}
        </select>
      </div>

      <div className="registration__field registration__field--checkbox">
        <label htmlFor="registration-field-estimated">
          <input
            id="registration-field-estimated"
            type="checkbox"
            checked={draft.isEstimatedIdentity}
            disabled={busy}
            aria-describedby="registration-field-estimated-hint"
            onChange={(event) => update('isEstimatedIdentity', event.target.checked)}
          />{' '}
          {t('registration:field.isEstimatedIdentity')}
        </label>

        <p id="registration-field-estimated-hint" className="registration__hint">
          {t('registration:fieldHint.isEstimatedIdentity')}
        </p>
      </div>

      <TextField
        field="bed"
        value={draft.bed}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('bed', value)}
        t={t}
      />

      {/* Announced, because a refusal is the whole message on this screen and the clinician's
          attention is on the number they just tried to file. */}
      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'problem'
                ? notice.message
                : notice.kind === 'unavailable'
                  ? registrationUnavailableMessage(t)
                  : registrationRefusalMessage(notice.code, notice.params, t)}
            </p>
          </div>
        )}
      </div>

      <footer className="registration__actions">
        {/* The one thing about registering that a clinician needs to know once, said where the
            decision is made rather than in a dialogue that gets clicked through. */}
        <p className="registration__final">{t('registration:screen.irreversible')}</p>

        <button
          type="button"
          className="registration__register"
          // Disabled only while a write is in flight — a second press would register a second
          // patient, and there is no `DELETE` (AGENTS.md §3). Never disabled for a problem in the
          // form; see `submit`.
          disabled={busy}
          onClick={submit}
        >
          {busy ? t('registration:screen.registering') : t('registration:screen.register')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('registration:screen.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('registration:screen.discardQuestion')}
        >
          <p>{t('registration:screen.discardQuestion')}</p>

          {/* Keep filling it in is first and is the safe one. The destructive button says what it
              does rather than "OK" — this is the click that loses what was typed. */}
          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('registration:screen.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('registration:screen.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
